import { useEffect, useId, useRef, useState } from "react"

import { ReactComponent as SvgChevron } from "@common_assets/svg/chevron-down.svg"
import { ReactComponent as SvgFinest } from "@common_assets/svg/finest.svg"
import {
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material"
import { useQueryAdminDashboardStudentAttention } from "api/reactQuery/queries/adminDashboard"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import {
  IDashboardPeriodParams,
  IDashboardStudentAttentionRow,
} from "ts/interfaces/AdminDashboard"
import { DashboardStudentAttentionView } from "ts/types/AdminDashboard"

import { sectionToggleChipSx } from "./AdminDashboard.styles"
import { Dash } from "./DashboardCells"
import DashboardSection from "./DashboardSection"
import DashboardSectionBody, { useShownValue } from "./DashboardSectionBody"
import PositivityChip from "./PositivityChip"

const SHORTLIST_ROWS = 5

const VIEWS: DashboardStudentAttentionView[] = [
  "fewest_bonuses_earned",
  "lowest_positivity",
  "most_fined",
  "lowest_attendance",
]

const formatName = (row: IDashboardStudentAttentionRow) =>
  [row.first_name, row.last_name].filter(Boolean).join(" ") || "—"

// The first column names the row and stays put while the rest scroll on mobile.
const stickySx = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  bgcolor: "mockup.neutral100",
}

// The full name shows on hover only when the column cuts it short.
const StudentName = ({ name }: { name: string }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const handleOpen = () => {
    const el = ref.current
    if (el && el.scrollWidth > el.clientWidth) setIsOpen(true)
  }

  return (
    <Tooltip
      title={name}
      open={isOpen}
      onOpen={handleOpen}
      onClose={() => setIsOpen(false)}
    >
      <Box
        ref={ref}
        component="span"
        sx={{
          display: "block",
          // Fills the column without widening it, so a long name is cut
          // instead of pushing the other columns out.
          width: 0,
          minWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          // On the span: the table's cell rule outranks a cell's sx
          fontWeight: 700,
        }}
      >
        {name}
      </Box>
    </Tooltip>
  )
}

const Shortlist = ({
  label,
  view,
  rows,
  skeletonRows,
}: {
  label: string
  view: DashboardStudentAttentionView
  rows: IDashboardStudentAttentionRow[]
  // First load: the real headers over this many placeholder rows.
  skeletonRows?: number
}) => {
  const { t } = useTranslation()
  // Lowest attendance ranks by attendance, so it takes the Fines column's place.
  const isAttendanceView = view === "lowest_attendance"
  const headers = [
    t("adminDashboard.column_student"),
    isAttendanceView
      ? t("adminDashboard.column_attendance")
      : t("adminDashboard.fines"),
    t("adminDashboard.bonuses"),
    t("adminDashboard.column_positivity"),
  ]

  return (
    <Box
      role="region"
      aria-label={label}
      aria-busy={skeletonRows ? true : undefined}
      tabIndex={0}
      sx={{
        overflowX: "auto",
        // Anchors the visually hidden cell text, as in DashboardTable
        position: "relative",
        borderRadius: "8px",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "mockup.primary60",
          outlineOffset: "2px",
        },
      }}
    >
      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root": {
            px: 1,
            py: 0,
            height: 44,
            borderBottom: "1px solid",
            borderBottomColor: "mockup.neutral90",
            fontSize: "14px",
            fontWeight: 500,
            color: "mockup.neutral10",
            whiteSpace: "nowrap",
          },
          // The value columns shrink to their content; the name takes the rest.
          "& .MuiTableCell-root:not(:first-of-type)": { width: "1%" },
          "& .MuiTableRow-head": { bgcolor: "transparent" },
          "& .MuiTableCell-head": {
            height: 32,
            borderBottom: "none",
            color: "mockup.neutral30",
            fontSize: "12px",
            fontWeight: 700,
            textTransform: "uppercase",
          },
        }}
      >
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell
                key={header}
                align={index > 0 ? "right" : undefined}
                sx={index === 0 ? { ...stickySx, zIndex: 2 } : undefined}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: skeletonRows ?? 0 }, (_, index) => (
            <TableRow key={index} data-testid="attention-skeleton-row">
              <TableCell colSpan={headers.length}>
                <Skeleton variant="rounded" height={28} />
              </TableCell>
            </TableRow>
          ))}
          {rows.map((row) => (
            <TableRow key={row.fk_student_id}>
              <TableCell
                component="th"
                scope="row"
                // The row's stripe, still opaque over the scrolled columns
                // Below this the table scrolls instead of squeezing the name
                sx={{ ...stickySx, bgcolor: "inherit", minWidth: 104 }}
              >
                <StudentName name={formatName(row)} />
              </TableCell>
              <TableCell align="right">
                {isAttendanceView ? (
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {row.attendance_count === null ? (
                      <Dash label={t("adminDashboard.attendanceNoData")} />
                    ) : (
                      row.attendance_count.toLocaleString("en-US")
                    )}
                  </Box>
                ) : (
                  <Box
                    component="span"
                    sx={{ color: "mockup.error50", fontWeight: 600 }}
                  >
                    {row.fine_count.toLocaleString("en-US")}
                  </Box>
                )}
              </TableCell>
              <TableCell align="right">
                {row.bonus_count.toLocaleString("en-US")}
              </TableCell>
              <TableCell align="right">
                <PositivityChip positivity={row.positivity} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

const StudentsAttention = ({
  params,
  onApply,
}: {
  params: IDashboardPeriodParams
  // Ranks the All students table like the chosen view.
  onApply: (view: DashboardStudentAttentionView) => void
}) => {
  const { t } = useTranslation()
  const viewLabelId = useId()
  const [view, setView] = useState<DashboardStudentAttentionView>(
    "fewest_bonuses_earned",
  )
  const query = useQueryAdminDashboardStudentAttention(params, view)
  const shownView = useShownValue(view, query.isPreviousData)
  // Shown once BED says the school has attendance data to rank by.
  const views = query.data?.data.has_attendance_integration
    ? VIEWS
    : VIEWS.filter((value) => value !== "lowest_attendance")
  // BED returns up to 10 rows in rank order; the shortlist shows the top 5.
  const rows = query.data?.data.rows.slice(0, SHORTLIST_ROWS) ?? []
  const emptyText = t(`adminDashboard.attentionEmpty_${shownView}`)
  const shortlistLabel = t("adminDashboard.attentionShortlist")

  // Read out once a view switch or a period change has loaded; silent on the
  // first load and on background refreshes.
  const queryKey = JSON.stringify([params, view])
  const lastQueryKey = useRef(queryKey)
  const isAnnouncePending = useRef(false)
  const [announcement, setAnnouncement] = useState("")

  useEffect(() => {
    if (lastQueryKey.current === queryKey) return
    lastQueryKey.current = queryKey
    isAnnouncePending.current = true
    // Emptied first, so the same text is read out again.
    setAnnouncement("")
  }, [queryKey])

  useEffect(() => {
    if (!isAnnouncePending.current || !query.data || query.isPreviousData)
      return
    isAnnouncePending.current = false
    setAnnouncement(
      rows.length
        ? t("adminDashboard.attentionShowing", {
            view: t(`adminDashboard.attentionView_${view}`),
            count: rows.length,
          })
        : emptyText,
    )
  }, [query.data, query.isPreviousData, queryKey])

  return (
    <DashboardSection
      title={t("adminDashboard.studentsNeedAttention")}
      question={t("adminDashboard.studentsNeedAttentionQuestion")}
      questionGap={1}
      icon={
        <Box
          component={SvgFinest}
          aria-hidden
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        />
      }
    >
      <Stack rowGap={2}>
        <Box sx={visuallyHiddenSx} id={viewLabelId}>
          {t("adminDashboard.attentionViewLabel")}
        </Box>
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          role="group"
          aria-labelledby={viewLabelId}
        >
          {views.map((value) => (
            <Chip
              key={value}
              label={t(`adminDashboard.attentionView_${value}`)}
              onClick={() => setView(value)}
              aria-pressed={view === value}
              sx={{
                ...sectionToggleChipSx(view === value),
                "& .MuiChip-label": {
                  ...sectionToggleChipSx(view === value)["& .MuiChip-label"],
                  fontWeight: 700,
                },
              }}
            />
          ))}
        </Stack>
        <DashboardSectionBody
          queries={[query]}
          isEmpty={!rows.length}
          emptyText={emptyText}
          skeleton={
            <Shortlist
              label={shortlistLabel}
              view={view}
              rows={[]}
              skeletonRows={SHORTLIST_ROWS}
            />
          }
        >
          <Stack rowGap={2}>
            <Shortlist label={shortlistLabel} view={shownView} rows={rows} />
            {/* While another view loads, Apply already uses that view. */}
            <Button
              variant="text"
              onClick={() => onApply(view)}
              endIcon={
                <Box
                  component={SvgChevron}
                  aria-hidden
                  sx={{ width: 20, height: 20, transform: "rotate(-90deg)" }}
                />
              }
              sx={{ alignSelf: "center" }}
            >
              {t("adminDashboard.applyToTable")}
            </Button>
          </Stack>
        </DashboardSectionBody>
      </Stack>
      <Box role="status" sx={visuallyHiddenSx}>
        {announcement}
      </Box>
    </DashboardSection>
  )
}

export default StudentsAttention
